/**
 * El modelo de la página de precios: los mismos números y reglas que el bloque
 * de planes y calculadora que xotski.com enmarca (build del 29 sep 2026,
 * `public/embed/pricing/index.html` en onxite/ox-xotski-web). Sin una sola
 * cadena visible: los textos viven en `pricingCopy.ts` y toman las cifras de
 * aquí a través de `vars()`, así que un precio que cambie en el catálogo
 * cambia también el texto.
 *
 * Es PURO a propósito: la misma configuración da siempre la misma cotización,
 * y la página sólo pinta lo que sale de aquí. Los precios base de los cuatro
 * niveles y los juegos de Free se pisan con el catálogo (`/plans/catalog`)
 * cuando llega, para que la tarjeta nunca diga algo distinto de lo que cobra
 * Stripe o de lo que la cuenta tiene de verdad.
 */

export type TierId = 'free' | 'xk1' | 'xk2' | 'enterprise';
export type PathId = TierId | 'alc';

export interface Tier {
  name: string;
  base: number;
  events: number | null;
  projects: number | null;
  brands: number | null;
  xotskies: number | null;
  types: number | null;
  languages: number | null;
  seats: number;
  grace: number;
  /** Los juegos: la lista con nombre (Free) o cuántos hay (los de pago). */
  games: string[] | number;
  pdf: boolean;
  demographics: boolean;
  leadGen: 'none' | 'addon' | 'included';
  liveOps: boolean;
  sso: boolean;
  /** «Desde»: el precio es un suelo, no una tarifa. */
  isFloor?: boolean;
}

/** Precios unitarios de los añadidos (los mismos SKU que el sitio). */
export const UNITS = {
  event: 100,
  brand: 100,
  xotskies100: 50,
  xotskiesPack: 100,
  type: 100,
  language: 100,
  leadGen: 100,
  editorSeatAnnual: 240,
  unlimitedEventsAddon: 1500,
  bespokeGame: 2500,
  premiumActivation: 250,
  whiteGlove: 500,
} as const;

/** Lo que incluye el camino a la carta antes de cobrar extras. */
export const ALC = { xotskies: 200, brands: 1, types: 2, languages: 1 } as const;

/** Límites universales que la copia cita. */
export const LIMITS = { gameSlotsPerStation: 8, testDeployPlays: 10, testDeployHours: 24 } as const;

export const TIERS: Record<TierId, Tier> = {
  free: { name: 'Free', base: 0, events: 3, projects: 1, brands: 1, xotskies: 50, types: 2, languages: 1, seats: 1, grace: 20, games: ['Spin Wheel', 'Scratch'], pdf: false, demographics: true, leadGen: 'none', liveOps: false, sso: false },
  xk1: { name: 'XK1', base: 1000, events: 15, projects: 2, brands: 2, xotskies: 200, types: 2, languages: 1, seats: 2, grace: 10, games: 8, pdf: false, demographics: true, leadGen: 'addon', liveOps: false, sso: false },
  xk2: { name: 'XK2', base: 2400, events: 30, projects: 4, brands: 4, xotskies: 300, types: 4, languages: 2, seats: 3, grace: 10, games: 8, pdf: true, demographics: true, leadGen: 'included', liveOps: true, sso: false },
  enterprise: { name: 'Enterprise', base: 6000, events: null, projects: null, brands: null, xotskies: null, types: null, languages: null, seats: 10, grace: 10, games: 8, pdf: true, demographics: true, leadGen: 'included', liveOps: true, sso: true, isFloor: true },
};

export const TIER_ORDER: TierId[] = ['free', 'xk1', 'xk2', 'enterprise'];
export const PATH_ORDER: PathId[] = ['free', 'alc', 'xk1', 'xk2', 'enterprise'];

export type NeedId = 'leadGen' | 'liveOps' | 'pdf' | 'sso';
export const NEED_IDS: NeedId[] = ['leadGen', 'liveOps', 'pdf', 'sso'];

export interface CalcState {
  e: number;
  x: number;
  b: number;
  t: number;
  l: number;
  pr: number;
  ed: number;
  needs: Record<NeedId, boolean>;
}

/** Abre en una forma que el nivel gratis cubre: el primer número que se ve es $0. */
export const INITIAL_STATE: CalcState = {
  e: 3, x: 50, b: 1, t: 2, l: 1, pr: 1, ed: 1,
  needs: { leadGen: false, liveOps: false, pdf: false, sso: false },
};

export type PresetId = 'single' | 'regional' | 'national' | 'org';
export interface Preset {
  id: PresetId;
  s: Omit<CalcState, 'needs'>;
  needs: Partial<Record<NeedId, boolean>>;
}
// El volumen de eventos nunca lleva a Enterprise. Sólo SSO/SLA/CSM o más de diez asientos.
export const PRESETS: Preset[] = [
  { id: 'single', s: { e: 3, x: 200, b: 1, t: 2, l: 1, pr: 1, ed: 1 }, needs: {} },
  { id: 'regional', s: { e: 12, x: 250, b: 2, t: 2, l: 1, pr: 2, ed: 2 }, needs: {} },
  { id: 'national', s: { e: 30, x: 300, b: 4, t: 4, l: 2, pr: 4, ed: 3 }, needs: { leadGen: true, liveOps: true, pdf: true } },
  { id: 'org', s: { e: 60, x: 300, b: 4, t: 4, l: 2, pr: 4, ed: 3 }, needs: { leadGen: true, liveOps: true, pdf: true } },
];

export type MainInputKey = 'e' | 'x' | 'b';
export const MAIN_INPUTS: Array<{ key: MainInputKey; min: number; max: number; step: number }> = [
  { key: 'e', min: 1, max: 120, step: 1 },
  { key: 'x', min: 50, max: 2000, step: 50 },
  { key: 'b', min: 1, max: 24, step: 1 },
];
export type DetailKey = 't' | 'l' | 'pr' | 'ed';
export const DETAIL_ROWS: Array<{ key: DetailKey; min: number; max: number }> = [
  { key: 't', min: 1, max: 20 },
  { key: 'l', min: 1, max: 8 },
  { key: 'pr', min: 1, max: 20 },
  { key: 'ed', min: 1, max: 24 },
];

export type LineId =
  | 'base.xk1' | 'base.xk2' | 'base.enterprise' | 'seat.editor'
  | 'events' | 'eventsOver' | 'unlimitedAddon'
  | 'brands' | 'brandsOver' | 'xotskies' | 'xotskiesOver'
  | 'types' | 'typesOver' | 'languages' | 'languagesOver'
  | 'leadGen' | 'seatsExtra';

export interface QuoteLine {
  id: LineId;
  amount: number;
  included?: boolean;
}

/** Por qué la configuración exige un nivel mínimo. `{n}` lo rellena `whyVars`. */
export type ReasonId =
  | 'projectsFree' | 'projectsXk1' | 'projectsXk2' | 'seatsEnt' | 'liveOps' | 'pdf' | 'sso';

export interface QuotePath {
  path: PathId;
  name: string;
  /** `null` cuando la configuración exige un nivel superior al de este camino. */
  total: number | null;
  /** El total calculado aunque no sea elegible (lo usa el checkout). */
  computed: number;
  lines: QuoteLine[];
  unlimited?: boolean;
}

export interface Quote {
  paths: Record<PathId, QuotePath>;
  rec: PathId;
  floor: PathId;
  why: ReasonId | null;
  alcTotal: number;
  /** À la carte sólo se ofrece cuando ya no se cabe en Free. */
  alcShown: boolean;
  /** Cabe en Free en todo salvo en el número de eventos: el muro. */
  freeWall: boolean;
}

export interface QuoteOptions {
  /** Cómo se factura el lead-gen: un pago único o uno por evento. */
  leadGenBilling?: 'flat' | 'perEvent';
}

export function fmt(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

/** La cotización, tal cual la hace el bloque del sitio (`quote()`), sin cadenas. */
export function quote(s: CalcState, T: Record<TierId, Tier>, opts: QuoteOptions = {}): Quote {
  const u = UNITS;
  const over = (v: number, base: number | null) => (base == null ? 0 : Math.max(0, v - base));
  const overXots = (base: number | null) =>
    base == null ? 0 : Math.ceil(Math.max(0, s.x - base) / u.xotskiesPack) * u.xotskies100 * s.e;
  const leadGenCost = (opts.leadGenBilling ?? 'flat') === 'flat' ? u.leadGen : u.leadGen * s.e;

  // En un objeto y no en dos `let`: TypeScript no ve las asignaciones que hace
  // el cierre `raise` y estrecharía `floor` a 'free' para siempre.
  const st: { floor: PathId; why: ReasonId | null } = { floor: 'free', why: null };
  const raise = (t: PathId, reason: ReasonId) => {
    if (PATH_ORDER.indexOf(t) > PATH_ORDER.indexOf(st.floor)) {
      st.floor = t;
      st.why = reason;
    }
  };
  if (s.pr > (T.free.projects ?? Infinity)) raise('xk1', 'projectsFree');
  if (s.pr > (T.xk1.projects ?? Infinity)) raise('xk2', 'projectsXk1');
  if (s.needs.liveOps) raise('xk2', 'liveOps');
  if (s.needs.pdf) raise('xk2', 'pdf');
  if (s.pr > (T.xk2.projects ?? Infinity)) raise('enterprise', 'projectsXk2');
  if (s.ed > T.enterprise.seats) raise('enterprise', 'seatsEnt');
  if (s.needs.sso) raise('enterprise', 'sso');
  const eligible = (id: PathId) => PATH_ORDER.indexOf(id) >= PATH_ORDER.indexOf(st.floor);

  const f = T.free;
  const freeShape =
    eligible('free') &&
    s.b <= (f.brands ?? 0) && s.x <= (f.xotskies ?? 0) && s.t <= (f.types ?? 0) &&
    s.l <= (f.languages ?? 0) && s.pr <= (f.projects ?? 0) && s.ed <= f.seats && !s.needs.leadGen;
  // Free es un muro: no hay precio por evento en Free.
  const freeOk = freeShape && s.e <= (f.events ?? 0);
  const freeWall = freeShape && s.e > (f.events ?? 0);
  const free: QuotePath = { path: 'free', name: f.name, total: freeOk ? 0 : null, computed: 0, lines: [] };

  const alcItems: QuoteLine[] = ([
    { id: 'seat.editor', amount: s.ed * u.editorSeatAnnual },
    { id: 'events', amount: s.e * u.event },
    { id: 'brands', amount: over(s.b, ALC.brands) * u.brand },
    { id: 'xotskies', amount: overXots(ALC.xotskies) },
    { id: 'types', amount: over(s.t, ALC.types) * u.type },
    { id: 'languages', amount: over(s.l, ALC.languages) * u.language },
    { id: 'leadGen', amount: s.needs.leadGen ? leadGenCost : 0 },
  ] as QuoteLine[]).filter((i) => i.amount > 0);
  const alcTotal = alcItems.reduce((a, i) => a + i.amount, 0);
  // À la carte sólo se ofrece a quien ya no cabe en Free.
  const alcShown = !freeOk && eligible('alc');
  const alc: QuotePath = { path: 'alc', name: 'À la carte', total: alcShown ? alcTotal : null, computed: alcTotal, lines: alcItems };

  const pkg = (id: TierId): QuotePath => {
    const k = T[id];
    let eventsOver = over(s.e, k.events) * u.event;
    let unlimited = false;
    if (id === 'xk2' && eventsOver > u.unlimitedEventsAddon) {
      eventsOver = u.unlimitedEventsAddon;
      unlimited = true;
    }
    const items: QuoteLine[] = ([
      { id: `base.${id}` as LineId, amount: k.base },
      unlimited ? { id: 'unlimitedAddon', amount: eventsOver } : { id: 'eventsOver', amount: eventsOver },
      { id: 'brandsOver', amount: over(s.b, k.brands) * u.brand },
      { id: 'xotskiesOver', amount: overXots(k.xotskies) },
      { id: 'typesOver', amount: over(s.t, k.types) * u.type },
      { id: 'languagesOver', amount: over(s.l, k.languages) * u.language },
      {
        id: 'leadGen',
        amount: s.needs.leadGen && k.leadGen === 'addon' ? leadGenCost : 0,
        included: s.needs.leadGen && k.leadGen === 'included',
      },
      { id: 'seatsExtra', amount: Math.max(0, s.ed - k.seats) * u.editorSeatAnnual },
    ] as QuoteLine[]).filter((i) => i.amount > 0 || i.included);
    const total = items.reduce((a, i) => a + (i.amount || 0), 0);
    return { path: id, name: k.name, total: eligible(id) ? total : null, computed: total, lines: items, unlimited };
  };

  const paths: Record<PathId, QuotePath> = {
    free, alc, xk1: pkg('xk1'), xk2: pkg('xk2'), enterprise: pkg('enterprise'),
  };

  let rec: PathId;
  if (st.floor === 'enterprise') rec = 'enterprise';
  else {
    const cands = [free, alc, paths.xk1, paths.xk2]
      .filter((p): p is QuotePath & { total: number } => p.total !== null)
      .sort((a, b) => a.total - b.total);
    rec = cands.length ? cands[0].path : 'enterprise';
    for (const cd of cands) {
      if (PATH_ORDER.indexOf(cd.path) > PATH_ORDER.indexOf(rec) && cd.total <= cands[0].total * 1.05) rec = cd.path;
    }
  }
  return { paths, rec, floor: st.floor, why: st.why, alcTotal, alcShown, freeWall };
}

/** El `{n}` de cada razón: el tope que se superó. */
export function whyVars(why: ReasonId, T: Record<TierId, Tier>): { n: number } {
  switch (why) {
    case 'projectsFree': return { n: T.free.projects ?? 0 };
    case 'projectsXk1': return { n: T.xk1.projects ?? 0 };
    case 'projectsXk2': return { n: T.xk2.projects ?? 0 };
    case 'seatsEnt': return { n: T.enterprise.seats };
    default: return { n: 0 };
  }
}

/** Nombre de pantalla de cada juego del catálogo (`allowedGames`). */
export const GAME_NAMES: Record<string, string> = {
  wheel: 'Spin Wheel',
  scratch: 'Scratch',
  xinkit: 'Xinkit',
  pirinola: 'Pirinola',
  slots: 'Slots',
  'random-draw': 'Random draw',
  soccer: 'Soccer',
  loteria: 'Lotería',
};

function gamesOf(allowed: string | null | undefined): string[] {
  return String(allowed ?? '')
    .split(',')
    .map((g) => g.trim())
    .filter(Boolean)
    .map((g) => GAME_NAMES[g] ?? g);
}

/**
 * Los niveles con lo que dice el catálogo encima de lo del build: el precio
 * base de cada uno y, para Free, los juegos que de verdad tiene la cuenta.
 */
export function tiersWithCatalog(
  catalog: Array<{ slug: string; value: number | string | null; allowedGames?: string | null }>,
): Record<TierId, Tier> {
  const out: Record<TierId, Tier> = {
    free: { ...TIERS.free }, xk1: { ...TIERS.xk1 }, xk2: { ...TIERS.xk2 }, enterprise: { ...TIERS.enterprise },
  };
  for (const id of TIER_ORDER) {
    const plan = catalog.find((p) => p.slug === id);
    if (!plan) continue;
    if (plan.value != null && Number.isFinite(Number(plan.value))) out[id].base = Number(plan.value);
    const games = gamesOf(plan.allowedGames);
    if (games.length) out[id].games = id === 'free' ? games : games.length;
  }
  return out;
}

export type PricingLang = 'en' | 'es';

/**
 * Todas las cifras que la copia cita, derivadas de los niveles (la misma
 * derivación que el `vars()` del sitio). Las cadenas sólo nombran estas claves.
 */
export function vars(T: Record<TierId, Tier>, lang: PricingLang): Record<string, string | number | null> {
  const u = UNITS;
  let alcFrom: number | null = null;
  for (let n = 1; n <= 500; n++) {
    if (u.editorSeatAnnual + n * u.event > T.xk1.base) { alcFrom = n; break; }
  }
  const and = lang === 'es' ? ' y ' : ' and ';
  const games = typeof T.xk1.games === 'number' ? T.xk1.games : T.xk1.games.length;
  const freeGames = Array.isArray(T.free.games) ? T.free.games.join(and) : String(T.free.games);
  return {
    freePrice: fmt(T.free.base), xk1Price: fmt(T.xk1.base), xk2Price: fmt(T.xk2.base), entPrice: fmt(T.enterprise.base),
    freeEvents: T.free.events, xk1Events: T.xk1.events, xk2Events: T.xk2.events,
    freeProjects: T.free.projects, xk1Projects: T.xk1.projects, xk2Projects: T.xk2.projects,
    freeBrands: T.free.brands, xk1Brands: T.xk1.brands, xk2Brands: T.xk2.brands,
    freeX: T.free.xotskies, xk1X: T.xk1.xotskies, xk2X: T.xk2.xotskies, alcX: ALC.xotskies,
    freeTypes: T.free.types, xk2Types: T.xk2.types, freeLangs: T.free.languages, xk2Langs: T.xk2.languages,
    freeSeats: T.free.seats, xk1Seats: T.xk1.seats, xk2Seats: T.xk2.seats, entSeats: T.enterprise.seats,
    freeGrace: T.free.grace, paidGrace: T.xk1.grace,
    games, freeGames,
    seat: fmt(u.editorSeatAnnual), seatMonth: fmt(u.editorSeatAnnual / 12),
    event: fmt(u.event), xotPack: u.xotskiesPack, xotPrice: fmt(u.xotskies100),
    type: fmt(u.type), language: fmt(u.language), brand: fmt(u.brand), leadGen: fmt(u.leadGen),
    unlimited: fmt(u.unlimitedEventsAddon), xk2Unlimited: fmt(T.xk2.base + u.unlimitedEventsAddon),
    bespoke: fmt(u.bespokeGame), premium: fmt(u.premiumActivation), whiteGlove: fmt(u.whiteGlove),
    slots: LIMITS.gameSlotsPerStation, testPlays: LIMITS.testDeployPlays, testHours: LIMITS.testDeployHours,
    alcFrom,
  };
}

/** `{clave}` → valor en toda la copia; lo que no está en `o` se deja tal cual. */
export function deepFill<V>(v: V, o: Record<string, string | number | null>): V {
  if (typeof v === 'string') {
    return v.replace(/\{(\w+)\}/g, (m, k: string) => (o[k] != null ? String(o[k]) : m)) as V;
  }
  if (Array.isArray(v)) return v.map((x: unknown) => deepFill(x, o)) as V;
  if (v && typeof v === 'object') {
    return Object.fromEntries(Object.entries(v as Record<string, unknown>).map(([k, x]) => [k, deepFill(x, o)])) as V;
  }
  return v;
}

/** Dónde manda el marco al visitante al pulsar (los mismos destinos que el build). */
export const HANDOFF = {
  /** Enterprise y los servicios: el formulario de xotski.com. */
  contact: 'https://xotski.com/form',
} as const;

/**
 * La configuración que el marco publica al sitio (`xotski:config`), con la
 * misma forma y el mismo orden de claves que `src/config/pricing.ts` de
 * onxite/ox-xotski-web: el sitio la compara como JSON y, si difiere, avisa por
 * consola y sigue nuestros números. Por eso sale de los niveles ya pisados por
 * el catálogo y no de constantes.
 */
export function pricingConfigOf(T: Record<TierId, Tier>, appRegisterUrl: string) {
  const plan = (id: TierId) => {
    const k = T[id];
    const base = {
      sku: id,
      price: k.base,
      events: k.events,
      projects: k.projects,
      brands: k.brands,
      xotskiesPerEvent: k.xotskies,
      types: k.types,
      languages: k.languages,
      editorSeats: k.seats,
      grace: k.grace,
      games: k.games,
      leadGen: k.leadGen,
    };
    return k.isFloor ? { ...base, priceIsFloor: true } : base;
  };
  const u = UNITS;
  return {
    plans: { free: plan('free'), xk1: plan('xk1'), xk2: plan('xk2'), enterprise: plan('enterprise') },
    alaCarte: { xotskiesPerEvent: ALC.xotskies, brands: ALC.brands, types: ALC.types, languages: ALC.languages },
    addons: {
      editorSeat: { sku: 'seat.editor', price: u.editorSeatAnnual, unit: 'year' },
      event: { sku: 'addon.event', price: u.event, unit: 'event' },
      xotskies: { sku: 'addon.xotskies100', price: u.xotskies100, unit: 'event', pack: u.xotskiesPack },
      type: { sku: 'addon.type', price: u.type, unit: 'project' },
      language: { sku: 'addon.language', price: u.language, unit: 'project' },
      brand: { sku: 'addon.brand', price: u.brand, unit: 'year' },
      leadGen: { sku: 'addon.leadgen', price: u.leadGen, unit: 'flat', perEvent: false },
      unlimitedEvents: { sku: 'addon.unlimited_events', price: u.unlimitedEventsAddon, unit: 'year', eligible: 'xk2' },
      bespokeGame: { sku: 'addon.bespoke_game', price: u.bespokeGame, unit: 'game', priceIsFloor: true },
      premiumActivation: { sku: 'addon.premium_activation', price: u.premiumActivation, unit: 'event' },
      whiteGlove: { sku: 'service.white_glove', price: u.whiteGlove, unit: 'project', priceIsFloor: true },
      atledi: { sku: 'addon.atledi_tokens', available: false },
    },
    limits: { ...LIMITS },
    links: { app: appRegisterUrl, contact: HANDOFF.contact },
  };
}

/**
 * La URL a la que el marco manda al visitante: el alta con la configuración
 * entera (`plan`, `events`, …), o el formulario de contacto para Enterprise.
 * Son los mismos parámetros que `PricingPage` lee al abrir.
 */
export function handoffUrl(tier: PathId, s: CalcState, lang: PricingLang, appRegisterUrl: string): string {
  const q = new URLSearchParams({
    plan: tier,
    events: String(s.e), xotskies: String(s.x), brands: String(s.b), types: String(s.t),
    languages: String(s.l), projects: String(s.pr), editors: String(s.ed), lang,
  });
  const needs = NEED_IDS.filter((k) => s.needs[k]);
  if (needs.length) q.set('needs', needs.join(','));
  if (tier === 'enterprise') {
    q.delete('plan');
    q.set('source', 'enterprise');
    return `${HANDOFF.contact}?${q.toString()}`;
  }
  return `${appRegisterUrl}?${q.toString()}`;
}

/** Dirección a la que va la configuración de Enterprise / servicio. Confirmar con Marco. */
export const ENTERPRISE_CONTACT_EMAIL = 'sales@onxite.com';
